import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Landmark,
  Calendar,
  Clock,
  FileText,
  Users,
  Package,
  X,
  CheckCircle2,
  CircleDashed,
  Hammer,
  Lightbulb,
  Upload,
  Camera,
  Image,
  Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAttachmentUrl = (att) =>
  att.storage_type === "cloud" ? `${API}/storage/${att.filename}` : `${API}/uploads/${att.filename}`;

// Station type tags
const STATION_TAGS = [
  { value: "STEAM", label: "STEAM", color: "bg-purple-100 text-purple-700" },
  { value: "Talking", label: "Talking", color: "bg-blue-100 text-blue-700" },
  { value: "Writing", label: "Writing", color: "bg-green-100 text-green-700" },
  { value: "Reading", label: "Reading", color: "bg-amber-100 text-amber-700" },
  { value: "Singing", label: "Singing", color: "bg-pink-100 text-pink-700" },
  { value: "Playing", label: "Playing", color: "bg-orange-100 text-orange-700" }
];

// Station statuses
const STATION_STATUSES = [
  { value: "idea_note", label: "Idea Note", icon: Lightbulb, color: "bg-gray-100 text-gray-600" },
  { value: "draft", label: "Draft", icon: FileText, color: "bg-yellow-100 text-yellow-700" },
  { value: "in_progress", label: "In Progress", icon: CircleDashed, color: "bg-blue-100 text-blue-700" },
  { value: "built", label: "Built", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" }
];

const initialFormData = {
  name: "",
  station_tags: [],
  supply_list: [],
  staff_instructions: "",
  kids_instructions: "",
  day_timestamp: "",
  status: "idea_note",
  run_start_date: "",
  run_end_date: "",
  notes: ""
};

export const LiteracyStations = ({ user, viewMode, userLookup = {} }) => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [supplyInput, setSupplyInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [formTab, setFormTab] = useState("info");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };
  
  // Check if user can edit/delete this station (author or supervisor/admin)
  const canEditStation = (station) => {
    if (!station || !user) return false;
    return station.created_by === user.id || user.role === "supervisor" || user.is_admin;
  };

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API}/literacy-stations`);
      setStations(response.data);
    } catch (error) {
      toast.error("Failed to fetch literacy stations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedStation) {
        await axios.put(`${API}/literacy-stations/${selectedStation.id}`, formData);
        toast.success("Literacy station updated successfully");
      } else {
        await axios.post(`${API}/literacy-stations`, formData);
        toast.success("Literacy station created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchStations();
    } catch (error) {
      toast.error(isEditing ? "Failed to update literacy station" : "Failed to create literacy station");
    }
  };

  const handleDelete = async () => {
    if (!selectedStation) return;
    try {
      await axios.delete(`${API}/literacy-stations/${selectedStation.id}`);
      toast.success("Literacy station deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedStation(null);
      fetchStations();
    } catch (error) {
      toast.error("Failed to delete literacy station");
    }
  };

  const handleEdit = (station) => {
    setSelectedStation(station);
    setFormData({
      name: station.name || "",
      station_tags: station.station_tags || [],
      supply_list: station.supply_list || [],
      staff_instructions: station.staff_instructions || "",
      kids_instructions: station.kids_instructions || "",
      day_timestamp: station.day_timestamp || "",
      status: station.status || "idea_note",
      run_start_date: station.run_start_date || "",
      run_end_date: station.run_end_date || "",
      notes: station.notes || ""
    });
    setIsEditing(true);
    setFormTab("info");
    setDialogOpen(true);
  };

  const updateStatus = async (station, newStatus) => {
    try {
      await axios.put(`${API}/literacy-stations/${station.id}`, {
        status: newStatus
      });
      toast.success(`Status updated to ${STATION_STATUSES.find(s => s.value === newStatus)?.label}`);
      fetchStations();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // File Upload Functions
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedStation) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/literacy-stations/${selectedStation.id}/attachments`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${files.length} file(s) uploaded successfully`);
      fetchStations();
      // Refresh selected station
      const updated = await axios.get(`${API}/literacy-stations/${selectedStation.id}`);
      setSelectedStation(updated.data);
    } catch (error) {
      toast.error("Failed to upload file(s)");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStation) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/literacy-stations/${selectedStation.id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo captured and uploaded");
      fetchStations();
      const updated = await axios.get(`${API}/literacy-stations/${selectedStation.id}`);
      setSelectedStation(updated.data);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedStation) return;
    try {
      await axios.delete(`${API}/literacy-stations/${selectedStation.id}/attachments/${attachmentId}`);
      toast.success("Attachment deleted");
      fetchStations();
      const updated = await axios.get(`${API}/literacy-stations/${selectedStation.id}`);
      setSelectedStation(updated.data);
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const isImageFile = (fileType) => {
    return fileType?.startsWith("image/");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedStation(null);
    setIsEditing(false);
    setSupplyInput("");
    setFormTab("info");
  };

  const toggleTag = (tag) => {
    const newTags = formData.station_tags.includes(tag)
      ? formData.station_tags.filter(t => t !== tag)
      : [...formData.station_tags, tag];
    setFormData({ ...formData, station_tags: newTags });
  };

  const addSupply = () => {
    if (supplyInput.trim()) {
      setFormData({
        ...formData,
        supply_list: [...formData.supply_list, supplyInput.trim()]
      });
      setSupplyInput("");
    }
  };

  const removeSupply = (index) => {
    setFormData({
      ...formData,
      supply_list: formData.supply_list.filter((_, i) => i !== index)
    });
  };

  const getStatusInfo = (status) => {
    return STATION_STATUSES.find(s => s.value === status) || STATION_STATUSES[0];
  };

  const getTagColor = (tag) => {
    return STATION_TAGS.find(t => t.value === tag)?.color || "bg-gray-100 text-gray-600";
  };

  const filteredStations = stations.filter((station) => {
    const matchesSearch = station.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      station.staff_instructions?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || station.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="stations-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="literacy-stations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Literacy Stations
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage interactive literacy learning stations
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-station-btn"
          className="btn-animate"
        >
          <Plus className="mr-2 h-4 w-4" /> New Station
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-stations"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATION_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATION_STATUSES.map((status) => {
          const count = stations.filter(s => s.status === status.value).length;
          const StatusIcon = status.icon;
          return (
            <Card
              key={status.value}
              className={`cursor-pointer transition-all ${statusFilter === status.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status.value ? "all" : status.value)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${status.color}`}>
                  <StatusIcon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-2xl font-semibold">{count}</p>
                  <p className="text-xs text-muted-foreground">{status.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Stations Grid */}
      {filteredStations.length === 0 ? (
        <Card className="border-stone-200" data-testid="no-stations">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Landmark className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">No literacy stations found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchQuery || statusFilter !== "all" ? "Try adjusting your filters" : "Create your first literacy station"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStations.map((station, index) => {
            const statusInfo = getStatusInfo(station.status);
            const StatusIcon = statusInfo.icon;
            return (
              <Card
                key={station.id}
                className="card-hover border-stone-200 animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`station-card-${station.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="font-heading text-lg line-clamp-2">
                        {station.name}
                      </CardTitle>
                      {/* Station Tags */}
                      {station.station_tags && station.station_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {station.station_tags.map((tag) => (
                            <Badge key={tag} className={`text-xs ${getTagColor(tag)}`}>
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {getStaffName(station.created_by) && (
                        <p className="text-xs text-blue-600">
                          Created by: {getStaffName(station.created_by)}
                        </p>
                      )}
                    </div>
                    {canEditStation(station) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`station-menu-${station.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleEdit(station)}
                          data-testid={`edit-station-${station.id}`}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        {STATION_STATUSES.filter(s => s.value !== station.status).map((status) => (
                          <DropdownMenuItem
                            key={status.value}
                            onClick={() => updateStatus(station, status.value)}
                          >
                            <status.icon className="mr-2 h-4 w-4" /> Mark as {status.label}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setSelectedStation(station);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-station-${station.id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Day Timestamp */}
                  {station.day_timestamp && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Clock className="h-4 w-4" />
                      <span>{station.day_timestamp}</span>
                    </div>
                  )}

                  {/* Run Dates */}
                  {(station.run_start_date || station.run_end_date) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Calendar className="h-4 w-4" />
                      <span className="font-mono text-xs">
                        {station.run_start_date || "?"} - {station.run_end_date || "?"}
                      </span>
                    </div>
                  )}

                  {/* Supply Count */}
                  {station.supply_list && station.supply_list.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Package className="h-4 w-4" />
                      <span>{station.supply_list.length} supplies needed</span>
                    </div>
                  )}

                  {/* Photos Count */}
                  {station.attachments && station.attachments.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Image className="h-4 w-4" />
                      <span>{station.attachments.length} photo(s)</span>
                    </div>
                  )}

                  {/* Photos Preview */}
                  {station.attachments && station.attachments.length > 0 && (
                    <div className="mb-3">
                      <div className="flex gap-2 overflow-x-auto">
                        {station.attachments.slice(0, 3).map((att) => (
                          <div key={att.id} className="flex-shrink-0">
                            {isImageFile(att.file_type) ? (
                              <img
                                src={getAttachmentUrl(att)}
                                alt={att.original_name}
                                className="h-12 w-12 object-cover rounded"
                              />
                            ) : (
                              <div className="h-12 w-12 bg-muted flex items-center justify-center rounded">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        ))}
                        {station.attachments.length > 3 && (
                          <div className="h-12 w-12 bg-muted flex items-center justify-center rounded text-xs text-muted-foreground">
                            +{station.attachments.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Instructions Preview */}
                  {station.staff_instructions && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Staff Setup</p>
                      <p className="text-sm line-clamp-2">{station.staff_instructions}</p>
                    </div>
                  )}

                  {/* Status */}
                  <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                    <Badge className={statusInfo.color}>
                      <StatusIcon className="mr-1 h-3 w-3" />
                      {statusInfo.label}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Literacy Station" : "Create New Literacy Station"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the station details" : "Set up a new interactive learning station"}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={formTab} onValueChange={setFormTab} className="w-full">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="info">Station Info</TabsTrigger>
              <TabsTrigger value="instructions">Instructions</TabsTrigger>
              <TabsTrigger value="supplies">Supplies</TabsTrigger>
              <TabsTrigger value="photos" disabled={!isEditing}>
                Photos {isEditing && `(${selectedStation?.attachments?.length || 0})`}
              </TabsTrigger>
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <TabsContent value="info" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Station Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter station name"
                        required
                        data-testid="station-name-input"
                      />
                    </div>

                    <div>
                      <Label>Station Type Tags</Label>
                      <p className="text-xs text-muted-foreground mb-2">Select all that apply</p>
                      <div className="flex flex-wrap gap-2">
                        {STATION_TAGS.map((tag) => (
                          <Badge
                            key={tag.value}
                            className={`cursor-pointer transition-all ${
                              formData.station_tags.includes(tag.value)
                                ? tag.color
                                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                            }`}
                            onClick={() => toggleTag(tag.value)}
                            data-testid={`tag-${tag.value.toLowerCase()}`}
                          >
                            {tag.label}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="day_timestamp">Day Timestamp Label</Label>
                      <Input
                        id="day_timestamp"
                        value={formData.day_timestamp}
                        onChange={(e) => setFormData({ ...formData, day_timestamp: e.target.value })}
                        placeholder="e.g., Monday Morning, Week 3"
                        data-testid="day-timestamp-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="status">Status *</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATION_STATUSES.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              <div className="flex items-center gap-2">
                                <status.icon className="h-4 w-4" />
                                {status.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Run Dates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="run_start_date">Start Date</Label>
                      <Input
                        id="run_start_date"
                        type="date"
                        value={formData.run_start_date}
                        onChange={(e) => setFormData({ ...formData, run_start_date: e.target.value })}
                        data-testid="run-start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="run_end_date">End Date</Label>
                      <Input
                        id="run_end_date"
                        type="date"
                        value={formData.run_end_date}
                        onChange={(e) => setFormData({ ...formData, run_end_date: e.target.value })}
                        data-testid="run-end-date-input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={2}
                    data-testid="station-notes-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="instructions" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Set Up Instructions for Staff</h3>
                  <Textarea
                    value={formData.staff_instructions}
                    onChange={(e) => setFormData({ ...formData, staff_instructions: e.target.value })}
                    placeholder="Enter detailed setup instructions for staff members..."
                    rows={6}
                    data-testid="staff-instructions-input"
                  />
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Instructions for Kids</h3>
                  <Textarea
                    value={formData.kids_instructions}
                    onChange={(e) => setFormData({ ...formData, kids_instructions: e.target.value })}
                    placeholder="Enter simple, clear instructions for children..."
                    rows={6}
                    data-testid="kids-instructions-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="supplies" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Supply List</h3>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={supplyInput}
                      onChange={(e) => setSupplyInput(e.target.value)}
                      placeholder="Add a supply item"
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSupply())}
                      data-testid="supply-input"
                    />
                    <Button type="button" variant="outline" onClick={addSupply} data-testid="add-supply-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.supply_list.length > 0 ? (
                    <div className="space-y-2">
                      {formData.supply_list.map((supply, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <span>{supply}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSupply(index)}
                            className="h-8 w-8 text-muted-foreground hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-muted/20 rounded-lg">
                      <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No supplies added yet</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Add items needed for this station
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="photos" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Photos & Files</h3>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                    />
                    <input
                      type="file"
                      ref={cameraInputRef}
                      className="hidden"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="take-photo-btn"
                    >
                      <Camera className="mr-2 h-4 w-4" /> Take Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-file-btn"
                    >
                      <Upload className="mr-2 h-4 w-4" /> Upload
                    </Button>
                  </div>
                </div>

                {uploading && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </div>
                )}
                
                {selectedStation?.attachments && selectedStation.attachments.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedStation.attachments.map((att) => (
                      <div key={att.id} className="relative group">
                        <div className="border rounded-lg overflow-hidden bg-muted/20">
                          {isImageFile(att.file_type) ? (
                            <img
                              src={getAttachmentUrl(att)}
                              alt={att.original_name}
                              className="w-full h-32 object-cover"
                            />
                          ) : (
                            <div className="w-full h-32 flex flex-col items-center justify-center">
                              <FileText className="h-10 w-10 text-muted-foreground mb-2" />
                              <span className="text-xs text-muted-foreground truncate px-2 max-w-full">
                                {att.original_name}
                              </span>
                            </div>
                          )}
                          <div className="p-2 border-t">
                            <p className="text-xs truncate">{att.original_name}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(att.file_size)}</p>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <a
                            href={getAttachmentUrl(att)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="p-1.5 bg-white rounded-full shadow hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-muted/20 rounded-lg">
                    <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No photos or files yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Take a photo or upload files to document this station
                    </p>
                  </div>
                )}
              </TabsContent>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-station-btn">
                  {isEditing ? "Update Station" : "Create Station"}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Literacy Station</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedStation?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-station"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
